import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, inject, input, output } from '@angular/core';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { formatLongDate } from '../card/schedule.card.model';
import { EXCEPTION_ORIGIN_LABELS, ExceptionRow } from './exceptions.list.model';
import { exceptionsListBridgeStyles, exceptionsListStyleId } from './exceptions.list.theme';

/**
 * The one way an exception is listed anywhere in the product: plan blackouts, line blackouts, and a
 * task's own skips and extra dates all render through these rows.
 *
 * The list is deliberately dumb. It takes flattened rows and reports a removal by id; it never reads a
 * plan, a line, or a schedule. That is what lets the same component sit on three screens whose records
 * share nothing but the shape of an exception, and it keeps the decision of what is removable with the
 * host that knows which document it is saving.
 */
@Component({
  selector: 'wb-schedule-exceptions-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: 'exceptions.list.component.html',
  styleUrls: ['./exceptions.list.component.less'],
})
export class ScheduleExceptionsListComponent {

  private readonly document = inject(DOCUMENT);

  public readonly $rows = input.required<readonly ExceptionRow[]>({ alias: 'rows' });

  public readonly $emptyText = input<string>('No exceptions', { alias: 'emptyText' });

  public readonly remove = output<string>();

  public constructor() {

    DesignSystem.Mount(this.document, exceptionsListStyleId, exceptionsListBridgeStyles);
  }

  /** `Fri 25 Dec 2026` for a day, `Fri 25 Dec 2026 – Mon 4 Jan 2027` for a range. */
  protected describe(row: ExceptionRow): string {

    if (row.date) return formatLongDate(row.date);
    if (!row.from) return '';
    if (!row.to || row.to === row.from) return formatLongDate(row.from);

    return `${formatLongDate(row.from)} – ${formatLongDate(row.to)}`;
  }

  protected originLabel(row: ExceptionRow): string {

    return row.origin ? EXCEPTION_ORIGIN_LABELS[row.origin] : '';
  }

  protected onRemove(row: ExceptionRow): void {

    this.remove.emit(row.id);
  }
}
